'use client';

import { useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselControls,
  CarouselNavigation,
  CarouselDots,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import type { PortraitTeaserItem } from '@/app/_components/shared/portrait-teaser/portrait-teaser.types';
import { getItemTrackId } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { useResponsiveSlidesPerView } from '@/app/_hooks/use-responsive-slides-per-view';
import { PortraitTeaserCard } from './portrait-teaser-card';

interface PortraitTeaserCarouselProps {
  items: PortraitTeaserItem[];
  showNavigation?: boolean;
  showDots?: boolean;
  autoplay?: boolean;
  priorityFirstItem?: boolean;
  trackingPrefix?: string;
}

export function PortraitTeaserCarousel({
  items,
  showNavigation = true,
  showDots = true,
  autoplay = false,
  priorityFirstItem,
  trackingPrefix,
}: PortraitTeaserCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const currentSlidesPerView = useResponsiveSlidesPerView(4);
  const canScroll = items.length > currentSlidesPerView;
  const carouselLabel = useLabel('carousel.portraitTeaser', 'Teaser');

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: 'start',
        loop: autoplay && canScroll,
        watchDrag: canScroll,
      }}
      autoplay={autoplay && canScroll}
      ariaLabel={carouselLabel}
    >
      <CarouselContent className="ml-portrait-teaser-ml-mobile xl:ml-portrait-teaser-ml-desktop">
        {items.map((item, index) => (
          <CarouselItem
            key={item.id ? String(item.id) : `portrait-teaser-${index}`}
            className="basis-full pl-portrait-teaser-gap-mobile md:basis-1/2 lg:basis-1/4 xl:pl-portrait-teaser-gap-desktop"
          >
            <PortraitTeaserCard
              item={item}
              priority={priorityFirstItem && index === 0}
              trackingPrefix={
                trackingPrefix
                  ? `${trackingPrefix}-link-${getItemTrackId(item.id, index)}`
                  : undefined
              }
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      {(showNavigation || showDots) && canScroll && (
        <CarouselControls className="mt-6">
          {showNavigation && <CarouselNavigation api={api} />}
          {showDots && <CarouselDots api={api} />}
        </CarouselControls>
      )}
    </Carousel>
  );
}
